import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import AppState from '@webapp/store/state';

import moment, { Moment } from 'moment';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';
import { getWidth } from '@components/styles';

import Flex from '@components/layout/flex';
import Button from '@components/actions/button';
import Typography from '@components/basics/typography';
import CurvedArrow from '@components/basics/curved-arrow';
import MotionOpacity from '@components/motion/motion-opacity';
import DowngradePlanCard from '@webapp/controllers/pricing/downgrade-plan-card';
import { isEntryLevelPlan } from '../../../utils/subsciptions/is-entry-level-plan';
import { Subscription } from '../../../../../../services/subscription/types/subscription';
import { Order } from '../../../types/orders/order';
import { CustomerMetadata } from '../../../../../../services/payment/types/api/customer-metadata';
import {
  CancelSubscription,
  CreateSubscription,
} from '../../../store/subscription/action-creators';
import SimpleArtist from '../../../../../../common/types/artist/simple-artist';
import { CallHistoryMethodAction, push } from 'connected-react-router';

interface PlanDowngradePageProps {
  className?: string;
  artist?: SimpleArtist;
  currentSubscription?: Subscription;
  currentOrder?: Order;
  currentPlan: string;
  selectedPlan: string;
  expiresAt?: Moment;
  isMobile: boolean;
  goToPayment: () => CallHistoryMethodAction;
  createSubscription: (
    currentSubscription: undefined | Subscription,
    order: Order,
    customer: CustomerMetadata
  ) => CreateSubscription;
  cancelSubscription: () => CancelSubscription;
  goBack: () => CallHistoryMethodAction;
}

const mapStateToProps = (state: AppState, props: any) => {
  const { clientId, id } = props.match?.params;

  const foundSubscription = state.subscriptionManager.summary?.artists
    .find((s) => s.id === clientId)
    ?.subscriptions.find((s) => s.id === id);

  const currentOrder = state.orders.basket.find(
    (p) => p.product.category === 'PLAN'
  );

  return {
    currentSubscription: foundSubscription?.subscription,
    artist: state.artist.myArtists.find((a) => a.id === clientId),
    currentPlan: foundSubscription?.plan || '',
    expiresAt: foundSubscription?.nextBilling
      ? moment(foundSubscription?.nextBilling)
      : undefined,
    currentOrder,
    selectedPlan: currentOrder?.product.name || '',
    isMobile: state.ui.mobile,
  };
};

const mapDispatchToProps = (dispatch: Dispatch, props: any) => {
  const { clientId, id } = props.match?.params;

  return {
    cancelSubscription: () => dispatch(new CancelSubscription(clientId)),
    goBack: () => dispatch(push(`/plans/${clientId}/${id}`)),
    goToPayment: () => dispatch(push(`/payment/${clientId}/${id || ''}`)),
    createSubscription: (
      currentSubscription: undefined | Subscription,
      order: Order,
      customer: CustomerMetadata
    ) =>
      dispatch(
        new CreateSubscription(
          undefined,
          undefined,
          clientId,
          order,
          customer,
          'artist',
          currentSubscription,
          undefined,
          undefined,
          undefined,
          undefined,
          'plan downgrade'
        )
      ),
  };
};

const PlanDowngradePage: React.FC<PlanDowngradePageProps> = ({
  ...props
}) => {

  const handleDowngrade = () => {
    if (!(props.currentOrder && props.artist)) {
      return;
    }

    if (isEntryLevelPlan(props.currentOrder.product)) {
      if (props.currentSubscription) {
        return props.cancelSubscription();
      }

      return props.createSubscription(
        undefined,
        props.currentOrder,
        props.artist
      );
    }

    return props.goToPayment();
    // return props.createSubscription(props.currentSubscription, props.currentOrder, props.artist);
  };

  return (
    <MotionOpacity className={`${props.className || ''} mt-80`}>
      <PricingContainer>
        <Typography className="mb-40" type="preview-header">
          <Translate id="ONBOARDING.DOWNGRADE.TITLE" />
          {props.isMobile ? ' ' : <br />}
          {props.selectedPlan}
        </Typography>
        <Typography
          className="mb-30"
          type="section-title"
          weight={props.isMobile ? '700' : '400'}
        >
          <Translate id="ONBOARDING.DOWNGRADE.TEXT" />
        </Typography>
        <List className="mb-40">
          <StyledCurvedArrow />
          <DowngradePlanCard title={props.currentPlan} isNewPlan={false} />
          <DowngradePlanCard title={props.selectedPlan} isNewPlan={true} />
        </List>
        <Typography className="mb-40" opacity=".5">
          {props.currentPlan} <Translate id="ONBOARDING.DOWNGRADE.EXPIRE" />{' '}
          {props.expiresAt?.format('LL')}
        </Typography>
        <ButtonGroup>
          <Button buttonType="primary" onClick={() => props.goBack()}>
            <Translate id="ONBOARDING.DOWNGRADE.BTN.CANCEL" />
          </Button>
          <Button buttonType="secondary" onClick={handleDowngrade}>
            <Translate id="ONBOARDING.DOWNGRADE.BTN.DOWNGRADE" />
          </Button>
        </ButtonGroup>
      </PricingContainer>
    </MotionOpacity>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(PlanDowngradePage);

const PricingContainer = styled.div`
  margin: auto;
  max-width: ${getWidth(6)};

  @media ${({ theme }) => theme.device.tablet} {
    max-width: ${getWidth(8)};
  }
`;

const List = styled.div`
  position: relative;
`;

const StyledCurvedArrow = styled(CurvedArrow)`
  position: absolute;
  top: 50%;
  right: -5%;
  z-index: 999;
  transform: scaleX(-1) translate(0, -50%);

  @media ${({ theme }) => theme.device.tablet} {
    left: -15%;
    right: auto;
    transform: translate(0, -50%);
  }
`;

const ButtonGroup = styled(Flex)`
  flex-direction: column;
  @media ${({ theme }) => theme.device.tablet} {
    flex-direction: row;
  }
  button {
    margin-bottom: 5px;
    &:last-child {
      margin-bottom: 0;
    }
    @media ${({ theme }) => theme.device.tablet} {
      width: 50%;
      margin-right: 5px;
      margin-bottom: 0;

      &:last-child {
        margin-right: 0;
      }
    }
  }
`;
